import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Building2, User as UserIcon, CheckCircle2, PenLine, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad } from '../components/SignaturePad';
import { formatDateTime as formatDate } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export const Parcels = () => {
  const { user } = useAuth();
  const [parcels, setParcels] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tracking_number: '',
    resident_id: '',      // selected resident (drives recipient_unit + property_id)
    courier: '',
    notes: '',
  });

  const isSecurity = user?.role === 'security';
  const isResident = user?.role === 'resident';

  // Handover-with-proof modal state (security only)
  const [handoverParcel, setHandoverParcel] = useState(null);
  const [handoverForm, setHandoverForm] = useState({ receiver_name: '', notes: '', photo_data_url: '' });
  const [handoverSaving, setHandoverSaving] = useState(false);
  const signatureRef = useRef(null);
  const photoInputRef = useRef(null);

  const loadParcels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/parcels`, { withCredentials: true });
      setParcels(data);
    } catch (error) {
      console.error('Parcels: failed to load parcels', error);
      toast.error('Failed to load parcels');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadResidents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/security/residents`, { withCredentials: true });
      setResidents(data);
    } catch (err) {
      // Non-critical: unit-select dropdown will be empty and shows a hint.
      console.error('Parcels: failed to load residents', err);
    }
  }, []);

  useEffect(() => {
    loadParcels();
    if (isSecurity) loadResidents();
  }, [isSecurity, loadParcels, loadResidents]);

  const selectedResident = useMemo(
    () => residents.find(r => r._id === formData.resident_id),
    [residents, formData.resident_id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.resident_id || !selectedResident) {
      toast.error('Please select the recipient (unit)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/parcels`, {
        tracking_number: formData.tracking_number,
        recipient_unit: selectedResident.unit_number,
        property_id: selectedResident.property_id,
        courier: formData.courier,
        notes: formData.notes,
      }, { withCredentials: true });
      toast.success(`Parcel logged for ${selectedResident.name} (Unit ${selectedResident.unit_number})`);
      setShowForm(false);
      setFormData({ tracking_number: '', resident_id: '', courier: '', notes: '' });
      loadParcels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log parcel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledge = async (parcelId) => {
    if (!window.confirm('Confirm you have received this parcel?')) return;
    try {
      await axios.patch(`${API}/parcels/${parcelId}/acknowledge`, {}, { withCredentials: true });
      toast.success('Parcel marked as received');
      loadParcels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge parcel');
    }
  };

  const openHandover = (parcel) => {
    setHandoverParcel(parcel);
    setHandoverForm({
      receiver_name: parcel.resident_name || '',
      notes: '',
      photo_data_url: '',
    });
  };

  const closeHandover = () => {
    setHandoverParcel(null);
    setHandoverForm({ receiver_name: '', notes: '', photo_data_url: '' });
  };

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) {
      toast.error('Photo must be under 3 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setHandoverForm(f => ({ ...f, photo_data_url: String(reader.result || '') }));
    reader.readAsDataURL(file);
  };

  const submitHandover = async () => {
    if (!handoverForm.receiver_name.trim()) {
      toast.error('Enter the recipient name');
      return;
    }
    const signature = signatureRef.current?.getDataUrl?.();
    if (!signature) {
      toast.error('Please capture the recipient signature');
      return;
    }
    setHandoverSaving(true);
    try {
      await axios.patch(
        `${API}/parcels/${handoverParcel._id}/handover`,
        {
          receiver_name: handoverForm.receiver_name.trim(),
          signature_data_url: signature,
          photo_data_url: handoverForm.photo_data_url || null,
          notes: handoverForm.notes.trim() || null,
        },
        { withCredentials: true }
      );
      toast.success('Handover recorded');
      closeHandover();
      loadParcels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record handover');
    } finally {
      setHandoverSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="parcels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Parcel Tracking</h1>
          <p className="text-sm text-[#64748b]">
            {isSecurity ? 'Log and route parcels to the correct resident' : 'Track your parcel deliveries'}
          </p>
        </div>
        {isSecurity && (
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
            data-testid="log-parcel-button"
          >
            <Package className="mr-2 h-4 w-4" />
            {showForm ? 'Close' : 'Log Parcel'}
          </Button>
        )}
      </div>

      {isSecurity && showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="parcel-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Tracking Number *</Label>
                <Input
                  value={formData.tracking_number}
                  onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                  placeholder="e.g., TRK123456789"
                  required
                  data-testid="tracking-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Recipient Unit *</Label>
                <select
                  value={formData.resident_id}
                  onChange={(e) => setFormData({ ...formData, resident_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="recipient-unit-select"
                >
                  <option value="">Select unit...</option>
                  {residents.map(r => (
                    <option key={r._id} value={r._id}>
                      {r.unit_number} — {r.name} · {r.property_name}
                    </option>
                  ))}
                </select>
                {residents.length === 0 && (
                  <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                    No residents with unit numbers found. Ask management to create residents first.
                  </p>
                )}
              </div>
            </div>

            {selectedResident && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-md text-sm flex items-center gap-2" data-testid="selected-resident-preview">
                <UserIcon className="h-4 w-4 text-blue-600" />
                <span>Will notify <strong>{selectedResident.name}</strong> — Unit {selectedResident.unit_number} at {selectedResident.property_name}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Courier *</Label>
              <Input
                value={formData.courier}
                onChange={(e) => setFormData({ ...formData, courier: e.target.value })}
                placeholder="e.g., FedEx, UPS, DHL, Sri Lanka Post"
                required
                data-testid="courier-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Fragile, size, delivery instructions..."
                data-testid="parcel-notes-input"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-parcel-button">
                {submitting ? 'Logging...' : 'Log Parcel'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Parcels List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
          </div>
        ) : parcels.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
            <Package className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
            <p className="text-[#64748b]">No parcels to display</p>
          </Card>
        ) : (
          parcels.map((parcel, index) => {
            const isCollected = parcel.status === 'collected';
            return (
            <Card key={parcel._id || index} className="p-6 bg-white border border-[#e2e8f0] hover:shadow-sm transition-shadow" data-testid={`parcel-card-${index}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-md bg-[#ec4899]/20 flex items-center justify-center">
                      <Package className="h-5 w-5 text-[#ec4899]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{parcel.tracking_number}</h3>
                      <p className="text-sm text-[#64748b]">{parcel.courier}</p>
                    </div>
                  </div>
                  {parcel.notes && <p className="text-sm text-[#64748b] mb-3">{parcel.notes}</p>}
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      isCollected ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {isCollected ? 'Received' : 'Pending Pickup'}
                    </span>
                    <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full">Unit {parcel.recipient_unit}</span>
                    {parcel.resident_name && (
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                        <UserIcon className="inline h-3 w-3 mr-1" />{parcel.resident_name}
                      </span>
                    )}
                    {parcel.property_name && (
                      <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full">
                        <Building2 className="inline h-3 w-3 mr-1" />{parcel.property_name}
                      </span>
                    )}
                    {parcel.logged_by && <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full">Logged by {parcel.logged_by}</span>}
                    {isCollected && parcel.acknowledged_by && (
                      <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full" data-testid={`parcel-ack-badge-${index}`}>
                        <CheckCircle2 className="inline h-3 w-3 mr-1" />
                        Received by {parcel.acknowledged_by}{parcel.acknowledged_at ? ` · ${formatDate(parcel.acknowledged_at)}` : ''}
                      </span>
                    )}
                    {isCollected && parcel.handover_by_security && (
                      <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full" data-testid={`parcel-handover-badge-${index}`}>
                        <PenLine className="inline h-3 w-3 mr-1" />
                        Handed over by {parcel.handover_by_security}
                      </span>
                    )}
                  </div>

                  {/* Proof of receipt (visible to everyone once handover exists) */}
                  {isCollected && (parcel.handover_signature || parcel.handover_photo || parcel.handover_notes) && (
                    <div className="mt-4 p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-md" data-testid={`parcel-proof-${index}`}>
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-2">Proof of Receipt</p>
                      <div className="flex flex-wrap items-start gap-4">
                        {parcel.handover_signature && (
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#64748b] mb-1">Signature</p>
                            <img
                              src={parcel.handover_signature}
                              alt="Recipient signature"
                              className="h-20 w-48 bg-white border border-[#e2e8f0] rounded object-contain"
                              data-testid={`parcel-signature-img-${index}`}
                            />
                          </div>
                        )}
                        {parcel.handover_photo && (
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#64748b] mb-1">Photo</p>
                            <img
                              src={parcel.handover_photo}
                              alt="Handover photo"
                              className="h-20 w-20 object-cover rounded border border-[#e2e8f0]"
                              data-testid={`parcel-photo-img-${index}`}
                            />
                          </div>
                        )}
                        <div className="text-xs text-[#64748b] max-w-xs">
                          {parcel.handover_receiver_name && <p><strong className="text-[#0f172a]">Receiver:</strong> {parcel.handover_receiver_name}</p>}
                          {parcel.handover_notes && <p className="mt-1"><strong className="text-[#0f172a]">Notes:</strong> {parcel.handover_notes}</p>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <p className="text-xs text-[#64748b]">{formatDate(parcel.created_at)}</p>
                  {isResident && !isCollected && (
                    <Button
                      size="sm"
                      onClick={() => handleAcknowledge(parcel._id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`acknowledge-parcel-${index}`}
                    >
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Mark as Received
                    </Button>
                  )}
                  {isSecurity && !isCollected && (
                    <Button
                      size="sm"
                      onClick={() => openHandover(parcel)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`handover-parcel-${index}`}
                    >
                      <PenLine className="mr-1 h-4 w-4" /> Hand Over
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );})
        )}
      </div>

      {/* Handover modal (security only) */}
      {handoverParcel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeHandover}
          data-testid="handover-modal-overlay"
        >
          <Card
            className="p-6 bg-white w-full max-w-lg mx-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="handover-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Record Handover</h3>
                <p className="text-xs text-[#64748b]">
                  {handoverParcel.tracking_number} · Unit {handoverParcel.recipient_unit}
                  {handoverParcel.resident_name ? ` · ${handoverParcel.resident_name}` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeHandover} className="h-8 w-8 p-0" data-testid="close-handover-modal">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Receiver Name *</Label>
                <Input
                  value={handoverForm.receiver_name}
                  onChange={(e) => setHandoverForm({ ...handoverForm, receiver_name: e.target.value })}
                  placeholder="Who is picking up? (leave as-is if the resident)"
                  data-testid="handover-receiver-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Signature *</Label>
                  <button
                    type="button"
                    onClick={() => signatureRef.current?.clear?.()}
                    className="text-xs text-[#2563eb] hover:underline"
                    data-testid="signature-clear-button"
                  >
                    Clear
                  </button>
                </div>
                <SignaturePad ref={signatureRef} height={160} />
                <p className="text-xs text-[#64748b]">Ask the recipient to sign above using their finger or a stylus.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Optional Photo</Label>
                {handoverForm.photo_data_url ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={handoverForm.photo_data_url}
                      alt="Handover"
                      className="h-20 w-20 object-cover rounded border border-[#e2e8f0]"
                      data-testid="handover-photo-preview"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setHandoverForm({ ...handoverForm, photo_data_url: '' })}
                      data-testid="handover-photo-remove"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={onPhotoChange}
                      className="hidden"
                      data-testid="handover-photo-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => photoInputRef.current?.click()}
                      className="border-[#e2e8f0]"
                      data-testid="handover-photo-button"
                    >
                      <Camera className="mr-2 h-4 w-4" /> Take / Upload photo
                    </Button>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Notes (Optional)</Label>
                <Input
                  value={handoverForm.notes}
                  onChange={(e) => setHandoverForm({ ...handoverForm, notes: e.target.value })}
                  placeholder="e.g., ID checked, family member pickup, etc."
                  data-testid="handover-notes-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#e2e8f0]">
              <Button type="button" variant="outline" onClick={closeHandover}>Cancel</Button>
              <Button
                type="button"
                onClick={submitHandover}
                disabled={handoverSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="submit-handover-button"
              >
                {handoverSaving ? 'Saving…' : 'Confirm Handover'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
